import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Check, Coins, Crown, Loader, Sparkles, Zap, RefreshCw } from "lucide-react";

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollingStartedAt, setPollingStartedAt] = useState(null);
  const [pollingHint, setPollingHint] = useState("");
  const [toast, setToast] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get("/billing/catalog"),
        api.get("/billing/me"),
      ]);
      setCatalog(c.data);
      setMe(m.data);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Idempotent polling for a single session_id. Keeps retrying every 2s for
   * up to 60s. Stripe's payment_status flip can take 15-45s in test mode,
   * so we cannot give up early.
   */
  const pollSession = useCallback(async (sid, opts = {}) => {
    const { onSuccess, onFinalFail } = opts;
    let attempts = 0;
    let alive = true;
    setPolling(true);
    setPollingStartedAt(Date.now());
    setPollingHint("Stripe is confirming your payment — usually 10-30 seconds.");
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.fulfilled) {
          if (onSuccess) onSuccess(data);
          setPolling(false);
          setPollingHint("");
          setPollingStartedAt(null);
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setToast({ kind: "warn", msg: "Checkout expired. No charge was made." });
          setPolling(false);
          setPollingHint("");
          setPollingStartedAt(null);
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setPolling(false);
          setPollingHint("");
          setPollingStartedAt(null);
          return;
        }
      }
      if (!alive) return;
      // Adjust hint as time goes on so users don't think we're stuck.
      if (attempts === 8) setPollingHint("Still confirming... Stripe can take up to a minute. Don't close the tab.");
      if (attempts === 20) setPollingHint("Taking longer than usual. Your card hasn't been charged twice — we're just waiting on Stripe to confirm.");
      if (attempts < 30) {
        setTimeout(tick, 2000);
      } else {
        // 60s exhausted. Don't say "still processing" — be more helpful.
        setPolling(false);
        setPollingHint("");
        setPollingStartedAt(null);
        if (onFinalFail) onFinalFail();
      }
    };
    tick();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    refresh();
  }, [user, refresh, navigate]);

  // Stripe redirect handler + pending-txn auto-recover
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("status") === "cancel";
    if (cancelled) {
      setToast({ kind: "warn", msg: "Checkout cancelled. No charge made." });
      params.delete("status");
      setParams(params, { replace: true });
      return;
    }

    const handleSuccess = (data) => {
      if (data.kind === "credit_pack") {
        setToast({ kind: "success", msg: `Boom — ${data.credits_granted} credits added.` });
      } else if (data.kind === "pro") {
        setToast({ kind: "success", msg: `You're Pro now. Unlimited paywalled features for 30 days.` });
      } else if (data.kind === "featured_boost") {
        setToast({ kind: "success", msg: `⭐ Your résumé is now Featured in the Candidates Store for 30 days.` });
      }
      params.delete("session_id");
      setParams(params, { replace: true });
      refresh();
    };

    const handleFinalFail = (sessionId) => {
      // Don't show a confusing "still processing" toast — instead keep the
      // session reference and give the user a clear retry path.
      setToast({
        kind: "warn",
        msg: "Stripe is taking longer than usual to confirm your payment. Your card has NOT been charged twice. Click 'Re-check now' to retry, or refresh in a minute.",
      });
      // Leave the session_id in the URL so the user can refresh / click Re-check.
    };

    if (sid) {
      pollSession(sid, {
        onSuccess: handleSuccess,
        onFinalFail: () => handleFinalFail(sid),
      });
      return;
    }

    // No session_id in URL → check for any unfulfilled txn from the last 30 min
    // and auto-recover. This handles the "user closed the tab too early" case.
    (async () => {
      try {
        const { data } = await api.get("/billing/pending");
        const pending = (data?.pending || [])[0];
        if (pending?.session_id) {
          // Quietly recover — don't toast unless it succeeds.
          pollSession(pending.session_id, {
            onSuccess: handleSuccess,
            onFinalFail: () => {/* silent — let the user re-check manually */},
          });
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, refresh, pollSession]);

  const recheckPending = async () => {
    const sid = params.get("session_id");
    if (sid) {
      pollSession(sid, {
        onSuccess: (data) => {
          if (data.kind === "credit_pack") setToast({ kind: "success", msg: `Boom — ${data.credits_granted} credits added.` });
          else if (data.kind === "pro") setToast({ kind: "success", msg: `You're Pro now.` });
          else if (data.kind === "featured_boost") setToast({ kind: "success", msg: `⭐ Your résumé is now Featured in the Store.` });
          params.delete("session_id");
          setParams(params, { replace: true });
          refresh();
        },
        onFinalFail: () => setToast({ kind: "warn", msg: "Still no confirmation from Stripe. Try again in another minute." }),
      });
      return;
    }
    try {
      const { data } = await api.get("/billing/pending");
      const pending = (data?.pending || [])[0];
      if (pending?.session_id) {
        pollSession(pending.session_id, {
          onSuccess: (d) => {
            if (d.kind === "credit_pack") setToast({ kind: "success", msg: `Boom — ${d.credits_granted} credits added.` });
            else if (d.kind === "pro") setToast({ kind: "success", msg: `You're Pro now.` });
            else if (d.kind === "featured_boost") setToast({ kind: "success", msg: `⭐ Your résumé is now Featured in the Store.` });
            refresh();
          },
          onFinalFail: () => setToast({ kind: "warn", msg: "Still no confirmation. Email support if your card has been charged." }),
        });
      } else {
        setToast({ kind: "warn", msg: "No pending payments found." });
      }
    } catch {
      setToast({ kind: "warn", msg: "Couldn't reach the server. Try again." });
    }
  };

  const purchase = async (kind, packId = null) => {
    setBuying(packId || kind);
    try {
      const { data } = await api.post("/billing/checkout", {
        kind,
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBuying(null);
      setToast({ kind: "error", msg: e.response?.data?.detail || "Checkout failed." });
    }
  };

  if (loading || !catalog || !me) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 text-center">
          <Loader className="mx-auto animate-spin rm-text-orange" size={32} />
          <div className="mt-4 font-mono-rm uppercase text-xs tracking-widest" style={{ color: "#475569" }}>
            Loading your account…
          </div>
        </div>
      </div>
    );
  }

  const fmtPrice = (usd) => `$${usd.toFixed(2)}`;
  const discountActive = me.beta_lifetime_discount;
  const applyDiscount = (usd) => discountActive ? Math.round(usd * 0.8 * 100) / 100 : usd;

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-[90%]"
             data-testid="billing-toast"
             style={{
               background: toast.kind === "success" ? "#1A7F5C" : toast.kind === "warn" ? "#E8AF31" : "#DC2626",
               color: "white",
               border: "3px solid #0F1B2D",
               boxShadow: "4px 4px 0 0 #0F1B2D",
               padding: "12px 16px",
             }}>
          <div className="flex items-center gap-2">
            {toast.kind === "success" && <Check size={16} />}
            <span className="font-semibold">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="ml-auto opacity-70 hover:opacity-100">×</button>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-10">
        {polling && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3 flex-wrap"
            style={{ background: "#FFFBEB", borderColor: "#FF6A00", boxShadow: "4px 4px 0 0 #FF6A00" }}
            data-testid="billing-payment-confirming-banner"
          >
            <Loader size={20} className="animate-spin flex-shrink-0 mt-0.5" style={{ color: "#FF6A00" }} />
            <div className="flex-1 min-w-[240px]">
              <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>
                Confirming with Stripe
              </div>
              <div className="text-sm">
                {pollingHint || "Stripe is confirming your payment — usually 10-30 seconds."}
              </div>
              {pollingStartedAt && (
                <div className="font-mono-rm text-xs mt-1" style={{ color: "#475569" }}>
                  Elapsed: {Math.floor((Date.now() - pollingStartedAt) / 1000)}s · Don't close this tab.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Re-check button when we have a session_id but polling has finished */}
        {!polling && (params.get("session_id") || me?.has_pending) && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3 flex-wrap"
            style={{ background: "#FEF3C7", borderColor: "#F59E0B" }}
            data-testid="billing-recheck-card"
          >
            <RefreshCw size={20} className="flex-shrink-0 mt-0.5" style={{ color: "#92400E" }} />
            <div className="flex-1 min-w-[240px]">
              <div className="rm-label mb-1" style={{ color: "#92400E" }}>
                Payment still confirming?
              </div>
              <div className="text-sm">
                If your card was charged but credits haven't shown up yet, click Re-check and we'll
                ask Stripe again. Your card will <strong>not</strong> be double-charged.
              </div>
            </div>
            <button
              type="button"
              onClick={recheckPending}
              className="rm-btn rm-btn-primary text-xs px-3 py-2 flex-shrink-0"
              data-testid="billing-recheck-btn"
            >
              <RefreshCw size={12} /> Re-check now
            </button>
          </div>
        )}

        {catalog?.test_mode && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3 flex-wrap"
            style={{ background: "#FEF3C7", borderColor: "#F59E0B", boxShadow: "4px 4px 0 0 #F59E0B" }}
            data-testid="billing-test-mode-banner"
          >
            <span
              className="rm-tag flex-shrink-0"
              style={{ background: "#F59E0B", borderColor: "#92400E", color: "#0F1B2D", fontWeight: 700 }}
            >
              💳 TEST MODE
            </span>
            <div className="flex-1 min-w-[260px] text-sm">
              <strong>This is a beta environment — no real money will be charged.</strong>{" "}
              Use Stripe test cards to try the checkout flow. The most common one is{" "}
              <code
                style={{ background: "white", padding: "2px 6px", borderRadius: 4, fontWeight: 700, border: "2px solid #92400E" }}
              >
                4242 4242 4242 4242
              </code>{" "}
              with any future expiry, any 3-digit CVC, any ZIP.
              <a
                href="/beta-guide/#14-trying-the-payment-flow-no-real-money"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 underline font-semibold"
                style={{ color: "#92400E" }}
                data-testid="billing-test-mode-more-link"
              >
                More test cards →
              </a>
            </div>
          </div>
        )}

        <div className="mb-6 md:mb-8">
          <div className="font-display uppercase text-3xl md:text-4xl leading-none" style={{ color: "#0F1B2D" }}>
            Billing & Credits
          </div>
          <div className="mt-2 text-sm" style={{ color: "#475569" }}>
            Top up credits or go Pro for unlimited paywalled features.
          </div>
        </div>

        {/* Current status card */}
        <div className="rm-card-static p-5 md:p-6 mb-6"
             style={{ background: "white" }}
             data-testid="billing-status">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#475569" }}>
                Plan
              </div>
              <div className="mt-1 flex items-center gap-2">
                {me.is_pro ? (
                  <>
                    <Crown size={20} className="rm-text-orange" />
                    <span className="font-display text-2xl uppercase" style={{ color: "#0F1B2D" }}>Pro</span>
                  </>
                ) : (
                  <span className="font-display text-2xl uppercase" style={{ color: "#0F1B2D" }}>Free</span>
                )}
              </div>
              {me.is_pro && me.pro_expires_at && (
                <div className="text-xs mt-1" style={{ color: "#475569" }}>
                  Renews / expires {new Date(me.pro_expires_at).toLocaleDateString()}
                </div>
              )}
            </div>

            <div>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#475569" }}>
                Credits
              </div>
              <div className="mt-1 flex items-center gap-2" data-testid="credit-balance">
                <Coins size={20} className="rm-text-orange" />
                <span className="font-display text-2xl uppercase" style={{ color: "#0F1B2D" }}>
                  {me.credits_balance}
                </span>
              </div>
              <div className="text-xs mt-1" style={{ color: "#475569" }}>
                +{catalog.free_monthly_credits} free / month auto-refresh
              </div>
            </div>

            <div>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#475569" }}>
                First-time gifts
              </div>
              <div className="mt-1 space-y-0.5 text-sm" style={{ color: "#0F1B2D" }}>
                <div className="flex items-center gap-1.5">
                  {me.free_tailor_used ? <span style={{color:"#475569"}}>✓ used</span> : <Sparkles size={12} className="rm-text-orange" />}
                  <span>1 free Tailor</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {me.free_skill_used ? <span style={{color:"#475569"}}>✓ used</span> : <Sparkles size={12} className="rm-text-orange" />}
                  <span>1 free Skill Assessment</span>
                </div>
              </div>
            </div>
          </div>

          {discountActive && (
            <div className="mt-4 pt-4 flex items-center gap-2 text-sm"
                 style={{ borderTop: "1px solid #E2E8F0", color: "#1A7F5C", fontWeight: 600 }}
                 data-testid="beta-discount-badge">
              <Sparkles size={14} />
              Beta tester perk active — 20% off all purchases, forever.
            </div>
          )}
        </div>

        {/* Pro plan */}
        <div className="mb-8">
          <div className="font-display uppercase text-xl mb-3" style={{ color: "#0F1B2D" }}>
            Go Pro
          </div>
          <button
            onClick={() => purchase("pro")}
            disabled={buying === "pro" || me.is_pro}
            className="w-full text-left rm-card-static p-5 md:p-6 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #FF6A00 0%, #FFC145 100%)",
              borderColor: "#0F1B2D",
              boxShadow: "5px 5px 0 0 #0F1B2D",
            }}
            data-testid="upgrade-pro-btn"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <Crown size={32} style={{ color: "#0F1B2D" }} />
              <div className="flex-1 min-w-[200px]">
                <div className="font-display uppercase text-2xl md:text-3xl leading-none" style={{ color: "#0F1B2D" }}>
                  Pro · $29/mo
                </div>
                <div className="mt-2 text-sm" style={{ color: "#0F1B2D", fontWeight: 500 }}>
                  Unlimited Tailor, Skill Assessment, Adjacent Roles, Re-tailor, Mock Interview, ProMo voice.
                </div>
                {discountActive && (
                  <div className="mt-1 text-xs" style={{ color: "#0F1B2D", fontWeight: 700 }}>
                    Your beta price: <span className="line-through opacity-50">$29.00</span>{" "}
                    <span>$23.20/mo · lifetime</span>
                  </div>
                )}
              </div>
              <div className="font-display uppercase text-base px-4 py-2"
                   style={{ background: "#0F1B2D", color: "white" }}>
                {me.is_pro ? "Active ✓" : buying === "pro" ? "Loading…" : "Upgrade →"}
              </div>
            </div>
          </button>
        </div>

        {/* Credit packs */}
        <div className="mb-6">
          <div className="font-display uppercase text-xl mb-3" style={{ color: "#0F1B2D" }}>
            Buy more credits
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(catalog.credit_packs).map(([id, pack]) => {
              const final = applyDiscount(pack.amount_usd);
              return (
                <button
                  key={id}
                  onClick={() => purchase("credit_pack", id)}
                  disabled={buying === id}
                  className="rm-card-static p-5 text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  style={{
                    background: id === "medium" ? "#FFE49A" : "white",
                    borderColor: "#0F1B2D",
                    boxShadow: "4px 4px 0 0 #FF6A00",
                  }}
                  data-testid={`pack-${id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={18} className="rm-text-orange" />
                    <div className="font-display uppercase text-lg" style={{ color: "#0F1B2D" }}>
                      {pack.label}
                    </div>
                  </div>
                  <div className="font-display text-3xl mb-1" style={{ color: "#0F1B2D", letterSpacing: "-0.02em" }}>
                    {pack.credits} <span className="text-base">credits</span>
                  </div>
                  <div className="text-sm" style={{ color: "#0F1B2D" }}>
                    {discountActive ? (
                      <>
                        <span className="line-through opacity-50">{fmtPrice(pack.amount_usd)}</span>{" "}
                        <span className="font-bold">{fmtPrice(final)}</span>
                      </>
                    ) : (
                      <span className="font-bold">{fmtPrice(pack.amount_usd)}</span>
                    )}
                    {id === "medium" && (
                      <span className="ml-2 px-1.5 py-0.5 text-[9px] font-mono-rm uppercase tracking-widest"
                            style={{ background: "#FF6A00", color: "white" }}>
                        Best value
                      </span>
                    )}
                  </div>
                  <div className="mt-3 font-mono-rm uppercase text-[10px] tracking-widest"
                       style={{ color: "#475569" }}>
                    {buying === id ? "Loading…" : "Buy →"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Costs per action */}
        <div className="rm-card-static p-5"
             style={{ background: "#0F1B2D", borderColor: "#0F1B2D" }}
             data-testid="action-costs">
          <div className="font-display uppercase text-base mb-3" style={{ color: "#FFC145" }}>
            What each action costs
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {Object.entries(catalog.action_costs).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2"
                   style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,193,69,0.2)" }}>
                <span style={{ color: "white", fontWeight: 500 }}>
                  {k.replace(/_/g, " ")}
                </span>
                <span style={{ color: "#FFC145", fontWeight: 700 }}>{v}c</span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px]" style={{ color: "#CBD5E1" }}>
            Pro members pay zero credits. Free users get 10 credits/month + lifetime gifts of 1 free Tailor and 1 free Skill Assessment.
          </div>
        </div>

        {/* Refresh button */}
        <div className="mt-6 flex justify-center">
          <button onClick={refresh} className="rm-btn rm-btn-secondary text-xs px-3 py-1.5">
            <RefreshCw size={12} className={polling ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
